import React, { useState, useEffect } from 'react';
import { Link, useParams } from "react-router-dom";
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/themes/material_blue.css";
import { formatDate, getLocalDateString } from '../../../utils/dateUtils';
import Swal from 'sweetalert2';
import superAdminApiClient from '../../../api/superAdminApiClient';
import Preloader from '../../../components/common/Preloader';
import '../../../admin-panel/pages/leads/MyLeads.css';

function VendorLeads() {
    const { id } = useParams();

    const [vendor, setVendor] = useState(null);
    const [leads, setLeads] = useState([]);
    const [loading, setLoading] = useState(true);
    const [leadsLoading, setLeadsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [pagination, setPagination] = useState({ page: 1, limit: 10, total: 0, totalPages: 0 });
    const [filters, setFilters] = useState({ name: '', email: '', category: '', startDate: '', endDate: '', status: '' });
    const [suggestedCount] = useState(0);
    const [downloading, setDownloading] = useState(false);


    const fetchVendorDetails = async () => {
        try {
            const response = await superAdminApiClient.get(`/superadmin/vendors/${id}`);
            if (response.data && response.data.vendor) {
                setVendor(response.data.vendor);
            }
        } catch (err) {
            console.error("Failed to fetch vendor details:", err);
            setError("Failed to fetch vendor details");
        } finally {
            setLoading(false);
        }
    };

    const fetchLeads = async (page = 1) => {
        try {
            setLeadsLoading(true);
            const params = {
                page,
                limit: pagination.limit,
                name: filters.name,
                email: filters.email,
                category: filters.category,
                startDate: filters.startDate,
                endDate: filters.endDate,
                status: filters.status
            };
            const response = await superAdminApiClient.get(`/superadmin/vendors/${id}/leads`, { params });
            if (response.data.success) {
                setLeads(response.data.data);
                setPagination(response.data.pagination);
            }
        } catch (err) {
            console.error("Failed to fetch vendor leads:", err);
            setError("Failed to fetch vendor leads");
        } finally {
            setLeadsLoading(false);
        }
    };

    // Initial load
    useEffect(() => {
        fetchVendorDetails();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    // Filter load
    useEffect(() => {
        fetchLeads(1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters.category, filters.startDate, filters.endDate, filters.status]);

    // Page changes load
    useEffect(() => {
        fetchLeads(pagination.page);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pagination.page]);

    // Debounce name/email input filters
    useEffect(() => {
        const delayDebounceFn = setTimeout(() => {
            if (filters.name !== '' || filters.email !== '') {
                fetchLeads(1);
            }
        }, 500);

        return () => clearTimeout(delayDebounceFn);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters.name, filters.email]);

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({ ...prev, [name]: value }));
    };

    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= pagination.totalPages) {
            setPagination(prev => ({ ...prev, page: newPage }));
        }
    };

    // Export to CSV
    const handleDownload = async () => {
        if (downloading) return;
        try {
            setDownloading(true);
            const params = {
                page: 1,
                limit: 9999,
                name: filters.name,
                email: filters.email,
                category: filters.category,
                startDate: filters.startDate,
                endDate: filters.endDate,
                status: filters.status
            };
            const response = await superAdminApiClient.get(`/superadmin/vendors/${id}/leads`, { params });
            if (response.data.success) {
                const dataToDownload = response.data.data;
                const headers = ['SR No.', 'Customer Name', 'Phone', 'Email', 'Category', 'City', 'Date Sent', 'Status'];
                const rows = dataToDownload.map((lead, index) => [
                    index + 1,
                    lead.name || 'Guest',
                    lead.mobile || 'N/A',
                    lead.email || 'N/A',
                    lead.category || 'N/A',
                    lead.city || 'N/A',
                    formatDate(lead.VendorLeads?.[0]?.created_at || lead.created_at),
                    lead.VendorLeads?.[0]?.status || 'open'
                ]);

                const csvContent = [
                    headers.join(','),
                    ...rows.map(e => e.map(val => `"${String(val).replace(/"/g, '""')}"`).join(','))
                ].join('\n');

                const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
                const url = URL.createObjectURL(blob);
                const link = document.createElement("a");
                link.setAttribute("href", url);
                link.setAttribute("download", `Vendor_Leads_${new Date().toISOString().slice(0, 10)}.csv`);
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            }
        } catch (err) {
            console.error("Failed to download CSV data:", err);
            Swal.fire('Error', 'Failed to export filtered leads', 'error');
        } finally {
            setDownloading(false);
        }
    };

    const getAvatarColor = (name) => {
        const char = (name || 'G').charAt(0).toUpperCase();
        const code = char.charCodeAt(0);
        if (code % 3 === 0) return 'from-emerald-500 to-teal-600';
        if (code % 3 === 1) return 'from-orange-400 to-rose-500';
        return 'from-amber-400 to-orange-500';
    };

    const getCategoryBadge = (category) => {
        switch (category) {
            case 'cab':
                return <span className="chip bg-sky-100 text-sky-700 whitespace-nowrap"><i className="fa-solid fa-taxi mr-1"></i>Cab</span>;
            case 'hotels':
                return <span className="chip bg-fuchsia-100 text-fuchsia-700 whitespace-nowrap"><i className="fa-solid fa-hotel mr-1"></i>Hotels</span>;
            case 'holidays':
                return <span className="chip bg-emerald-100 text-emerald-700 whitespace-nowrap"><i className="fa-solid fa-plane mr-1"></i>Holidays</span>;
            case 'tirth_yatra':
                return <span className="chip bg-amber-100 text-amber-700 whitespace-nowrap"><i className="fa-solid fa-dharmachakra mr-1"></i>Tirth Yatra</span>;
            case 'medical_tourism':
                return <span className="chip bg-rose-100 text-rose-700 whitespace-nowrap"><i className="fa-solid fa-briefcase-medical mr-1"></i>Medical Tourism</span>;
            default:
                return <span className="chip bg-gray-100 text-gray-700 whitespace-nowrap text-capitalize">{category?.replace('_', ' ') || 'N/A'}</span>;
        }
    };

    const getStatusChip = (status) => {
        switch (status) {
            case 'open':
                return <span className="chip bg-blue-100 text-blue-700 whitespace-nowrap">Open</span>;
            case 'follow_up':
                return <span className="chip bg-amber-100 text-amber-700 whitespace-nowrap">Follow-up</span>;
            case 'closed':
                return <span className="chip bg-gray-100 text-gray-600 whitespace-nowrap">Closed</span>;
            case 'converted':
                return <span className="chip bg-emerald-100 text-emerald-700 whitespace-nowrap">Converted</span>;
            default:
                return <span className="chip bg-indigo-100 text-indigo-700 whitespace-nowrap text-capitalize">{status}</span>;
        }
    };

    const renderPageNumbers = () => {
        const pageNumbers = [];
        const maxVisible = 5;
        let startPage = Math.max(1, pagination.page - 2);
        let endPage = Math.min(pagination.totalPages, startPage + maxVisible - 1);

        if (endPage - startPage < maxVisible - 1) {
            startPage = Math.max(1, endPage - maxVisible + 1);
        }

        for (let i = startPage; i <= endPage; i++) {
            pageNumbers.push(i);
        }

        return (
            <div className="flex items-center gap-1.5 text-xs md:text-sm">
                <button
                    className={`page-btn px-3 py-2 rounded-lg border border-gray-200 bg-white transition ${pagination.page === 1 ? 'text-gray-300 cursor-not-allowed' : 'text-gray-600 hover:bg-gray-50'}`}
                    disabled={pagination.page === 1}
                    onClick={() => handlePageChange(pagination.page - 1)}
                >
                    <i className="fa-solid fa-chevron-left text-[10px]"></i> Prev
                </button>

                {startPage > 1 && (
                    <>
                        <button
                            className="page-btn px-3.5 py-2 rounded-lg border border-gray-200 bg-white text-gray-600 hover:bg-gray-50 transition"
                            onClick={() => handlePageChange(1)}
                        >
                            1
                        </button>
                        {startPage > 2 && <span className="px-1 text-gray-400">...</span>}
                    </>
                )}

                {pageNumbers.map(pageNo => (
                    <button
                        key={pageNo}
                        className={`page-btn px-3.5 py-2 rounded-lg transition ${pagination.page === pageNo ? 'active text-white font-bold' : 'border border-gray-200 bg-white text-gray-600 hover:bg-gray-50'}`}
                        onClick={() => handlePageChange(pageNo)}
                    >
                        {pageNo}
                    </button>
                ))}

                {endPage < pagination.totalPages && (
                    <>
                        {endPage < pagination.totalPages - 1 && <span className="px-1 text-gray-400">...</span>}
                        <button
                            className="page-btn px-3.5 py-2 rounded-lg border border-gray-200 bg-white text-gray-600 hover:bg-gray-50 transition"
                            onClick={() => handlePageChange(pagination.totalPages)}
                        >
                            {pagination.totalPages}
                        </button>
                    </>
                )}

                <button
                    className={`page-btn px-3 py-2 rounded-lg border border-gray-200 bg-white transition ${pagination.page === pagination.totalPages ? 'text-gray-300 cursor-not-allowed' : 'text-gray-600 hover:bg-gray-50'}`}
                    disabled={pagination.page === pagination.totalPages}
                    onClick={() => handlePageChange(pagination.page + 1)}
                >
                    Next <i className="fa-solid fa-chevron-right text-[10px]"></i>
                </button>
            </div>
        );
    };

    if (error) {
        return <div className="alert alert-danger p-4 m-4 text-center fw-bold">{error}</div>;
    }

    if (loading) {
        return <Preloader />;
    }

    const companyName = vendor?.company_name || vendor?.name || 'Vendor Profile';
    const initials = companyName.substring(0, 2).toUpperCase();
    const companyCity = vendor?.company_city || vendor?.city || 'India';

    return (
        <div className="my-leads-page-container">
            <div className="max-w-7xl mx-auto space-y-6">

                {/* Top Header */}
                <div className="d-flex flex-column flex-md-row justify-content-between align-items-center gap-3 bg-white p-3 rounded-3 shadow-sm border mb-4">
                    <div className="d-flex align-items-center gap-3">
                        <div 
                            className="d-flex align-items-center justify-content-center text-white fw-bold text-sm shadow-sm"
                            style={{
                                width: '44px',
                                height: '44px',
                                borderRadius: '12px',
                                background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 50%, #d946ef 100%)',
                                letterSpacing: '0.5px'
                            }}
                        >
                            {initials}
                        </div>
                        <div>
                            <h4 className="mb-0 fw-bold text-dark fs-5">
                                {companyName} Leads
                            </h4>
                            <div className="text-muted small d-flex align-items-center gap-1 mt-1">
                                <i className="fa-solid fa-location-dot text-danger"></i>
                                <span>{companyCity}</span>
                            </div>
                        </div>
                    </div>

                    {/* Right Stats Box */}
                    <div className="d-flex align-items-center gap-3 bg-light border rounded-3 px-3 py-2">
                        <div className="text-center px-2">
                            <span className="d-block text-uppercase text-muted fw-semibold" style={{ fontSize: '11px', letterSpacing: '0.5px' }}>
                                Lead Balance
                            </span>
                            <span className="fw-bold text-dark fs-6">
                                {vendor?.available_leads ?? 0}
                            </span>
                        </div>
                        <div className="vr" style={{ height: '28px', opacity: 0.2 }}></div>
                        <div className="text-center px-2">
                            <span className="d-block text-uppercase text-muted fw-semibold" style={{ fontSize: '11px', letterSpacing: '0.5px' }}>
                                KYC Status
                            </span>
                            <div className="d-flex align-items-center justify-content-center gap-1 mt-1">
                                <span 
                                    className="d-inline-block rounded-circle"
                                    style={{
                                        width: '8px',
                                        height: '8px',
                                        backgroundColor: vendor?.last_registration_step >= 6 ? '#10b981' : '#f59e0b'
                                    }}
                                ></span>
                                <span className={`fw-bold small ${vendor?.last_registration_step >= 6 ? 'text-success' : 'text-warning'}`}>
                                    {vendor?.last_registration_step >= 6 ? 'Verified' : 'Pending'}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Main Body */}
                <div className="bg-white p-6 rounded-2xl shadow-sm ring-1 ring-black/5 space-y-8">

                    {/* Performance Metrics */}
                    <div>
                        <h2 className="font-extrabold text-[var(--ink)] text-xs md:text-sm lg:text-base mb-4 tracking-wide flex items-center gap-2">
                            <span className="w-1.5 h-5 rounded-full bg-gradient-to-b from-indigo-500 to-fuchsia-500"></span>
                            Vendor Performance Summary (Super Admin View)
                        </h2>
                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">

                            {/* Total Enquiries */}
                            <div className="stat-card bg-gradient-to-br from-indigo-600 to-violet-600 text-white">
                                <div className="absolute -right-6 -top-6 w-24 h-24 bg-white/10 rounded-full"></div>
                                <div className="absolute right-3 bottom-3 text-2xl opacity-25"><i className="fa-solid fa-inbox"></i></div>
                                <p className="text-[11px] lg:text-xs text-indigo-100 font-semibold uppercase tracking-wide">Total Enquiries</p>
                                <p className="text-2xl lg:text-3xl font-extrabold mt-1.5 font-display">{pagination.total}</p>
                                <p className="text-[11px] text-indigo-100 mt-1 flex items-center gap-1"><i className="fa-solid fa-arrow-trend-up"></i> live count</p>
                            </div>

                            {/* Quotation Request */}
                            <div className="stat-card bg-white border border-amber-100">
                                <span className="absolute top-0 right-0 bg-[var(--marigold)] w-3.5 h-3.5 rounded-bl-full"></span>
                                <p className="text-[11px] lg:text-xs text-gray-400 font-semibold uppercase tracking-wide">Quotation Request</p>
                                <p className="text-2xl lg:text-3xl font-extrabold text-[var(--ink)] mt-1.5 font-display">
                                    {suggestedCount}
                                    <span className="chip text-amber-700 bg-amber-100 ml-1 align-middle">New</span>
                                </p>
                                <div className="mt-3 -mx-4 -mb-4 px-4 py-2.5 bg-gradient-to-r from-teal-600 to-emerald-600 text-white text-[11px] lg:text-xs text-center font-bold cursor-pointer hover:opacity-95 transition rounded-b-[18px]">
                                    Get Verified Leads <i className="fa-solid fa-chevron-right text-[8px] ml-0.5"></i>
                                </div>
                            </div>

                            {/* Prospect Lead */}
                            <div className="stat-card bg-white border border-emerald-100">
                                <p className="text-[11px] lg:text-xs text-gray-400 font-semibold uppercase tracking-wide">Prospect Lead</p>
                                <div className="flex items-baseline gap-2.5 mt-1.5">
                                    <p className="text-2xl lg:text-3xl font-extrabold text-[var(--ink)] font-display">10sec</p>
                                    <span className="chip text-emerald-700 bg-emerald-100 border border-emerald-200">Good</span>
                                </div>
                                <p className="text-[11px] text-gray-400 mt-2">Avg. response time</p>
                            </div>
                        </div>
                    </div>

                    {/* Promo Banner / Info Banner */}
                    <div className="bg-gradient-to-r from-fuchsia-600 via-violet-600 to-indigo-700 rounded-2xl p-6 lg:p-8 flex flex-col md:flex-row md:items-center md:justify-between text-white shadow-lg relative overflow-hidden promo-banner">
                        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-5 z-10 w-full md:w-auto">
                            <div className="bg-white/15 backdrop-blur-sm p-2.5 rounded-xl border border-white/25 shrink-0">
                                {vendor?.company_logo_path ? (
                                    <img
                                        src={vendor.company_logo_path}
                                        alt="Logo"
                                        className="w-16 h-16 rounded-lg object-cover shadow-inner bg-white"
                                    />
                                ) : (
                                    <div className="w-16 h-16 bg-gradient-to-br from-amber-400 to-orange-500 rounded-lg flex flex-col items-center justify-center font-black text-sm leading-none shadow-inner text-white font-display">
                                        <span>LOGO</span>
                                    </div>
                                )}
                            </div>
                            <div className="min-w-0 flex-1 w-full">
                                <h4 className="text-xl lg:text-2xl font-extrabold font-display break-words">{companyName}</h4>
                                <p className="text-sm lg:text-base text-violet-100 mt-1.5 flex items-center gap-1.5 truncate"><i className="fa-solid fa-location-dot shrink-0"></i> <span className="truncate">{companyCity}</span></p>
                                <p className="text-xs lg:text-sm text-violet-200 mt-2.5 max-w-xl break-words">
                                    Below is the full history of leads received by this vendor. Super Admins have full unmasked visibility to contact details.
                                </p>
                            </div>
                        </div>
                        <div className="absolute -right-12 -top-12 w-48 h-48 bg-white/10 rounded-full"></div>
                        <div className="absolute -left-10 -bottom-10 w-32 h-32 bg-white/10 rounded-full"></div>
                    </div>

                    {/* Recent Enquiries Table Container */}
                    <div className="border border-gray-100 rounded-2xl bg-white shadow-sm overflow-hidden">

                        {/* Card Header with Export Button */}
                        <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center bg-white">
                            <h2 className="font-extrabold text-[var(--ink)] text-sm md:text-base lg:text-lg tracking-wide flex items-center gap-2">
                                <span className="w-1.5 h-5 rounded-full bg-gradient-to-b from-teal-500 to-emerald-500"></span>
                                Assigned Leads History
                            </h2>
                            {downloading ? (
                                <span className="text-xs lg:text-sm text-gray-400 font-semibold flex items-center gap-1.5">
                                    <i className="fa-solid fa-spinner fa-spin text-[11px]"></i> Exporting...
                                </span>
                            ) : (
                                <button
                                    onClick={handleDownload}
                                    className="text-xs lg:text-sm text-indigo-600 font-semibold cursor-pointer hover:text-indigo-800 flex items-center gap-1.5 bg-none border-none outline-none"
                                >
                                    <i className="fa-solid fa-download text-[11px]"></i> Download
                                </button>
                            )}
                        </div>

                        {/* Filters Panel */}
                        <div className="px-6 py-4 bg-gray-50/50 border-b border-gray-100 flex flex-wrap gap-3 items-center">
                            <input
                                type="text"
                                name="name"
                                className="bg-white border border-gray-200 text-xs rounded-xl px-3 py-2 w-full sm:w-44 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition"
                                placeholder="Search Name..."
                                value={filters.name}
                                onChange={handleFilterChange}
                            />
                            <input
                                type="text"
                                name="email"
                                className="bg-white border border-gray-200 text-xs rounded-xl px-3 py-2 w-full sm:w-44 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition"
                                placeholder="Search Email..."
                                value={filters.email}
                                onChange={handleFilterChange}
                            />
                            <select
                                name="category"
                                className="bg-white border border-gray-200 text-xs rounded-xl px-3 py-2 w-full sm:w-40 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition"
                                value={filters.category}
                                onChange={handleFilterChange}
                            >
                                <option value="">All Categories</option>
                                <option value="holidays">Holidays</option>
                                <option value="hotels">Hotels</option>
                                <option value="cab">Cab</option>
                                <option value="tirth_yatra">Tirth Yatra</option>
                                <option value="medical_tourism">Medical Tourism</option>
                            </select>
                            <select
                                className="bg-white border border-gray-200 text-xs rounded-xl px-3 py-2 w-full sm:w-40 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition"
                                value={filters.status}
                                onChange={(e) => {
                                    setFilters(prev => ({ ...prev, status: e.target.value }));
                                }}
                            >
                                <option value="">All Statuses</option>
                                <option value="open">Open</option>
                                <option value="follow_up">Follow-up</option>
                                <option value="closed">Closed</option>
                            </select>
                            <div className="w-full sm:w-48">
                                <Flatpickr
                                    className="bg-white border border-gray-200 text-xs rounded-xl px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition"
                                    placeholder="Select Date Range"
                                    options={{
                                        mode: "range",
                                        dateFormat: "Y-m-d"
                                    }}
                                    value={filters.startDate && filters.endDate ? [filters.startDate, filters.endDate] : []}
                                    onChange={(dates) => {
                                        if (dates.length === 2) {
                                            const start = getLocalDateString(dates[0]);
                                            const end = getLocalDateString(dates[1]);
                                            setFilters(prev => ({ ...prev, startDate: start, endDate: end }));
                                        } else if (dates.length === 0) {
                                            setFilters(prev => ({ ...prev, startDate: '', endDate: '' }));
                                        }
                                    }}
                                />
                            </div>
                            <button
                                className="bg-gray-200 hover:bg-gray-300 text-gray-700 text-xs font-bold px-4 py-2 rounded-xl transition"
                                onClick={() => {
                                    setFilters({ name: '', email: '', category: '', startDate: '', endDate: '', status: '' });
                                }}
                            >
                                Reset
                            </button>
                        </div>

                        {/* Leads Table */}
                        <div className="overflow-x-auto">
                            {leadsLoading ? (
                                <div className="text-center p-8">
                                    <div className="spinner-border text-indigo-600" role="status">
                                        <span className="visually-hidden">Loading...</span>
                                    </div>
                                </div>
                            ) : (
                                <table className="w-full text-left border-collapse leads-table">
                                    <thead>
                                        <tr className="border-b border-gray-200">
                                            <th className="w-12">#</th>
                                            <th>Customer Name</th>
                                            <th>Phone</th>
                                            <th>Email</th>
                                            <th>Category</th>
                                            <th>City</th>
                                            <th>Date Sent</th>
                                            <th>Status</th>
                                            <th>Action</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {leads.length > 0 ? leads.map((lead, index) => {
                                            const startNo = (pagination.page - 1) * pagination.limit;
                                            const avatarLetter = (lead.name || 'G').charAt(0).toUpperCase();
                                            const avatarGrad = getAvatarColor(lead.name);

                                            return (
                                                <tr key={lead.id}>
                                                    <td>{startNo + index + 1}</td>
                                                    <td>
                                                        <div className="flex items-center gap-3">
                                                            <div className={`avatar bg-gradient-to-br ${avatarGrad}`}>
                                                                {avatarLetter}
                                                            </div>
                                                            <span className="font-bold text-[var(--ink)]">
                                                                {lead.name || 'Guest'}
                                                            </span>
                                                        </div>
                                                    </td>
                                                    <td className="text-indigo-600 font-semibold">
                                                        <a href={`tel:${lead.mobile}`} className="hover:underline flex items-center gap-1.5 whitespace-nowrap">
                                                            <i className="fa-solid fa-phone text-xs rotate-90 opacity-70"></i> {lead.mobile}
                                                        </a>
                                                    </td>
                                                    <td className="text-gray-500">{lead.email || 'N/A'}</td>
                                                    <td>{getCategoryBadge(lead.category)}</td>
                                                    <td className="text-gray-500">{lead.city || 'N/A'}</td>
                                                    <td className="text-gray-500 text-xs lg:text-sm">
                                                        {formatDate(lead.VendorLeads?.[0]?.created_at || lead.created_at)}
                                                    </td>
                                                    <td>{getStatusChip(lead.VendorLeads?.[0]?.status || 'open')}</td>
                                                    <td>
                                                        <Link to={`/superadmin/leads/detail/${lead.id}`} className="px-3 py-1.5 bg-indigo-50 text-[var(--indigo)] hover:bg-[var(--indigo)] hover:text-white rounded-lg text-xs font-semibold transition whitespace-nowrap">
                                                            View Details
                                                        </Link>
                                                    </td>
                                                </tr>
                                            );
                                        }) : (
                                            <tr>
                                                <td colSpan="9" className="text-center p-8 text-gray-500">
                                                    <div className="text-lg font-bold mb-1">No Leads Found</div>
                                                    <div className="text-xs text-gray-400">This vendor has no leads matching the criteria.</div>
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            )}
                        </div>

                        {/* Pagination Footer */}
                        {!leadsLoading && pagination.totalPages > 0 && (
                            <div className="px-6 py-4 bg-white border-t border-gray-100 flex flex-col sm:flex-row items-center justify-between gap-4">
                                <div className="text-xs md:text-sm text-gray-500">
                                    Showing <span className="font-semibold text-[var(--ink)]">{pagination.total > 0 ? (pagination.page - 1) * pagination.limit + 1 : 0}</span> to <span className="font-semibold text-[var(--ink)]">{Math.min(pagination.page * pagination.limit, pagination.total)}</span> of <span className="font-semibold text-[var(--ink)]">{pagination.total}</span> entries
                                </div>
                                {renderPageNumbers()}
                            </div>
                        )}

                    </div>

                </div>

            </div>
        </div>
    );
}

export default VendorLeads;
